import { forwardRef, type TextareaHTMLAttributes, type ReactNode } from "react";

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
	sizeVariant?: "default" | "large";
	label?: ReactNode;
	error?: string;
	required?: boolean;
	fullHeight?: boolean;
}

const sizeVariantLabelStyles: Record<NonNullable<TextareaProps["sizeVariant"]>, string> = {
	default: "mb-2 text-sm",
	large: "mb-2 text-base",
};

const sizeVariantTextareaStyles: Record<NonNullable<TextareaProps["sizeVariant"]>, string> = {
	default: "py-2 px-4 text-sm",
	large: "py-3 px-5 text-base",
};

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({ sizeVariant = "default", label, error, id, className = "", required, fullHeight, ...props }, ref) => {
	const textareaId = id || props.name;

	const containerClass = fullHeight ? "h-full flex flex-col" : "";
	const textareaClass = fullHeight ? "flex-1" : "";

	return (
		<div className={containerClass}>
			{label && (
				<label htmlFor={textareaId} className={`block font-medium text-primary font-sans ${sizeVariantLabelStyles[sizeVariant]}`}>
					{label}
					{required && <span className="text-red-500"> *</span>}
				</label>
			)}
			<textarea
				ref={ref}
				id={textareaId}
				className={`bg-white w-full border border-gray-300 rounded-lg font-sans focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed resize-none placeholder:text-gray-400 placeholder:font-light text-primary ${
					error ? "border-red-500" : ""
				} ${sizeVariantTextareaStyles[sizeVariant]} ${textareaClass} ${className}`}
				{...props}
			/>
			{error && <p className="text-xs text-red-500 mt-1">{error}</p>}
		</div>
	);
});

Textarea.displayName = "Textarea";

export default Textarea;
